class ProductItemGallery extends HTMLElement {
  connectedCallback() {
    this.gallery = this.querySelector('.product-item__gallery');
    this.prevArrow = this.querySelector('.product-item__gallery-arrow--prev');
    this.nextArrow = this.querySelector('.product-item__gallery-arrow--next');
    this.dots = Array.from(this.querySelectorAll('.product-item__gallery-dot'));
    this.currentIndex = 0;
    this._scrollAnimationId = null;

    if (!this.gallery) return;

    this.gallery.style.scrollBehavior = 'auto';   // native smooth scroll kapalı, kendi animasyonumuz var
    this.gallery.style.scrollSnapType = 'none';   // CSS'ten gelen snap, animasyonu kesmesin
    this.gallery.scrollLeft = 0;

    this._isDragging = false;
    this._dragMoved = false;

    this._onScroll = this.updateUI.bind(this);
    this._onClick = this.handleClick.bind(this);
    this._onPointerDown = this.handlePointerDown.bind(this);
    this._onPointerMove = this.handlePointerMove.bind(this);
    this._onPointerUp = this.handlePointerUp.bind(this);
    this._onDragStart = function (e) { e.preventDefault(); };
    this._stopPropagation = function (e) { e.stopPropagation(); };

    this.gallery.addEventListener('scroll', this._onScroll, { passive: true });
    this.addEventListener('click', this._onClick);
    this.gallery.addEventListener('pointerdown', this._onPointerDown);
    this.gallery.addEventListener('pointermove', this._onPointerMove);
    this.gallery.addEventListener('pointerup', this._onPointerUp);
    this.gallery.addEventListener('pointercancel', this._onPointerUp);
    this.gallery.addEventListener('dragstart', this._onDragStart);

    this.gallery.addEventListener('touchstart', this._stopPropagation, { passive: true });
    this.gallery.addEventListener('touchmove', this._stopPropagation, { passive: true });
    this.gallery.addEventListener('mousedown', this._stopPropagation);
    this.gallery.addEventListener('mousemove', this._stopPropagation);
    this.gallery.addEventListener('mouseup', this._stopPropagation);

    this._resizeObserver = new ResizeObserver(() => this.updateUI());
    this._resizeObserver.observe(this.gallery);

    this._intersectionObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          this.stopScrollAnimation();
          this.gallery.scrollLeft = 0;
          this.currentIndex = 0;
          this.updateUI();
        }
      });
    }, { threshold: 0.1 });
    this._intersectionObserver.observe(this);

    requestAnimationFrame(() => {
      this.gallery.scrollLeft = 0;
      this.updateUI();
    });
  }

  disconnectedCallback() {
    this.stopScrollAnimation();

    if (this.gallery) {
      this.gallery.removeEventListener('scroll', this._onScroll);
      this.gallery.removeEventListener('pointerdown', this._onPointerDown);
      this.gallery.removeEventListener('pointermove', this._onPointerMove);
      this.gallery.removeEventListener('pointerup', this._onPointerUp);
      this.gallery.removeEventListener('pointercancel', this._onPointerUp);
      this.gallery.removeEventListener('dragstart', this._onDragStart);
      this.gallery.removeEventListener('touchstart', this._stopPropagation);
      this.gallery.removeEventListener('touchmove', this._stopPropagation);
      this.gallery.removeEventListener('mousedown', this._stopPropagation);
      this.gallery.removeEventListener('mousemove', this._stopPropagation);
      this.gallery.removeEventListener('mouseup', this._stopPropagation);
    }
    this.removeEventListener('click', this._onClick);
    if (this._resizeObserver) this._resizeObserver.disconnect();
    if (this._intersectionObserver) this._intersectionObserver.disconnect();
  }

  stopScrollAnimation() {
    if (this._scrollAnimationId) {
      cancelAnimationFrame(this._scrollAnimationId);
      this._scrollAnimationId = null;
    }
  }

  animateScrollTo(targetLeft, duration = 380) {
    this.stopScrollAnimation();

    const startLeft = this.gallery.scrollLeft;
    const distance = targetLeft - startLeft;
    if (Math.abs(distance) < 1) return;

    const startTime = performance.now();
    const easeInOutCubic = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);

    const step = (now) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      this.gallery.scrollLeft = startLeft + distance * easeInOutCubic(progress);

      if (progress < 1) {
        this._scrollAnimationId = requestAnimationFrame(step);
      } else {
        this._scrollAnimationId = null;
      }
    };

    this._scrollAnimationId = requestAnimationFrame(step);
  }

  goToIndex(index) {
    if (!this.gallery || !this.dots.length) return;
    const targetIndex = Math.max(0, Math.min(index, this.dots.length - 1));
    this.currentIndex = targetIndex;
    this.animateScrollTo(targetIndex * this.gallery.clientWidth);
  }

  handlePointerDown(e) {
    if (e.pointerType !== 'mouse') return;
    if (e.button !== 0) return;

    e.preventDefault();
    e.stopPropagation();

    this.stopScrollAnimation();

    this._isDragging = true;
    this._dragMoved = false;
    this._dragStartX = e.clientX;
    this._dragStartScroll = this.gallery.scrollLeft;

    this.gallery.classList.add('is-dragging');

    try {
      this.gallery.setPointerCapture(e.pointerId);
    } catch (err) {}
  }

  handlePointerMove(e) {
    if (!this._isDragging) return;

    e.stopPropagation();

    const delta = e.clientX - this._dragStartX;

    if (Math.abs(delta) > 3) this._dragMoved = true;

    this.gallery.scrollLeft = this._dragStartScroll - delta;
  }

  handlePointerUp(e) {
    if (!this._isDragging) return;
  
    e.stopPropagation();
  
    this._isDragging = false;
    this.gallery.classList.remove('is-dragging');
  
    try {
      this.gallery.releasePointerCapture(e.pointerId);
    } catch (err) {}
  
    const slideWidth = this.gallery.clientWidth;
  
    if (slideWidth) {
      const SWIPE_THRESHOLD = Math.max(60, slideWidth * 0.2);
      const delta = e.clientX - this._dragStartX;
      let targetIndex = this.currentIndex;
  
      if (Math.abs(delta) > SWIPE_THRESHOLD) {
        targetIndex += delta < 0 ? 1 : -1;
      }
  
      this.goToIndex(targetIndex); // threshold'u geçmediyse zaten currentIndex'e geri yumuşakça döner
    }
  
    if (this._dragMoved) {
      setTimeout(() => {
        this._dragMoved = false;
      }, 300);
    }
  }

  updateUI() {
    if (!this.gallery) return;

    const slideWidth = this.gallery.clientWidth;

    if (slideWidth) {
      this.classList.add('is-ready');
    }

    if (this.prevArrow && this.nextArrow) {
      const maxScroll = this.gallery.scrollWidth - this.gallery.clientWidth;
      const atStart = this.gallery.scrollLeft <= 2;
      const atEnd = this.gallery.scrollLeft >= maxScroll - 2;

      this.prevArrow.classList.toggle('is-hidden', atStart);
      this.nextArrow.classList.toggle('is-hidden', atEnd || maxScroll <= 1);
    }

    if (this.dots.length && slideWidth) {
      const activeIndex = Math.round(this.gallery.scrollLeft / slideWidth);
      this.dots.forEach((dot, index) => {
        dot.classList.toggle('is-active', index === activeIndex);
      });
    }
  }

  handleClick(e) {
    if (this._dragMoved) {
      e.preventDefault();
      e.stopPropagation();
      this._dragMoved = false;
      return;
    }

    const arrow = e.target.closest('.product-item__gallery-arrow');
    if (arrow) {
      this.scrollByDirection(arrow.classList.contains('product-item__gallery-arrow--next') ? 1 : -1);
      return;
    }

    const dot = e.target.closest('.product-item__gallery-dot');
    if (dot) {
      const index = this.dots.indexOf(dot);
      if (index === -1) return;
      this.goToIndex(index);
    }
  }

  scrollByDirection(direction) {
    if (!this.gallery) return;
    this.goToIndex(this.currentIndex + direction);
  }
}

if (!customElements.get('product-item-gallery')) {
  customElements.define('product-item-gallery', ProductItemGallery);
}

window.addEventListener('pageshow', function () {
  document.querySelectorAll('product-item-gallery').forEach(function (el) {
    if (el.gallery) {
      if (typeof el.stopScrollAnimation === 'function') el.stopScrollAnimation();
      el.gallery.scrollLeft = 0;
      el.currentIndex = 0;
      if (typeof el.updateUI === 'function') el.updateUI();
    }
  });
});